// Signed in, nothing chosen: the activations this session may read.
//
// THIS SCREEN WAS A BOX YOU PASTED A UUID INTO, and the box was not a design.
// There was no endpoint answering "which activations may I read?", the four
// report endpoints all take an id, and a browser that derived a list from
// anything else would be a browser guessing — in the direction of showing
// something. `GET /v1/activations` exists as of 2026-08-28 and this screen asks
// it. Everything below is that answer, drawn.
//
// THE LIST IS THE SERVER'S AND IS NOT RE-SORTED HERE, WHICH IS THE HALF OF THE
// OLD RULE THAT SURVIVED. operator/src/activations.ts orders by last event with
// the ones nothing has arrived for at the top, because "an activation with a
// grant and nothing captured yet is the one somebody is about to work at" — a
// second sort in a browser is a second answer to the same question. The request
// still carries no arguments at all: what a caller may see is decided by the
// policies in 004 against the credential in the header, and check.mjs asserts
// the url has no query string on it.
//
// THE OTHER HALF SAID THERE WAS "NOTHING HERE TO PAGE, SEARCH OR NARROW BY, AND
// A FILTER ADDED TO THIS SCREEN WOULD BE THE FIRST THING ON IT THE DATABASE DID
// NOT DECIDE". THERE IS NOW A SEARCH BOX AND A STATE FILTER. The distinction the
// old paragraph missed is between narrowing WHAT MAY BE SEEN and narrowing WHAT
// IS DRAWN. A filter sent to the server is a second opinion about access and is
// still refused. A filter applied to rows already delivered discloses nothing
// that was not already on the screen a moment ago, and the alternative — a
// nova_admin with sixty activations scrolling for the one they want — is not
// neutrality, it is the same list with the work moved onto the reader.
//
// Both are drawn after the fact and neither reorders: the design's own note
// says "a filter changes what is shown, not the order, and the order is the
// server's", which is the rule above restated as a sentence on the page.
//
// NO COUNTS ON A ROW, AND THE ABSENCE IS LOAD-BEARING RATHER THAN A GAP TO FILL
// IN. Registrations, tablets, opt-in rate: every one of those is the headline of
// a report that records its own read against its own activation. A directory row
// carrying one would hand over every activation's headline against a single
// audit row saying somebody listed them, which xp/ACTIVATIONS-REPORT.md calls
// an audit bypass wearing a feature's clothes. The endpoint declines to send
// them; this screen must not compute them from anything else, and the check
// reads every number on a rendered ROW back against the server's own answer.
//
// HOW MANY ROWS THERE ARE IS NOT ONE OF THOSE, and the count line above the list
// says so out loud. "Six activations, two open" is a fact about the list this
// reader may read, which the service counts with the answer (151).
// It is not a figure about any activation, it tells them nothing about a show
// they could not already see, and it is what makes the filter honest — without
// it, a state filter that matches nothing is indistinguishable from a list that
// is empty. It lives outside the rows for exactly that reason, which is also why
// the check's digit scrape is scoped to rows and still passes unweakened.
//
// WHICH FOUR STATES, AND WHY THEY ARE NOT QUITE THE DESIGN'S. The prototype
// filters on active / paused / archived. The ruling of 12 Oct (C3) kept Open,
// Closed and Sealed and added Paused, which the service holds as `paused_at`
// (151). The service files each row (`state`, operator/src/activations.ts) and
// does the filtering: the State control asks GET /v1/activations?state=, and
// the counts beside it come back with the answer over every state, so a list
// filtered to one state still says what the others hold. "Archived" is still
// not a state, because it is a word people expect to be able to undo and a
// seal is not undone. A row's sentence about it is the server's `says`, and
// the tag is drawn from the same row, which is why they cannot disagree.
//
// WHAT A ROW SAYS IS WHAT THE EVENT IS, AND WHOSE IT IS SECOND.
//
// This paragraph read, until 2026-08-30: "There is no activation name and no
// venue anywhere in the schema — `activation` is an id, a client and three
// timestamps — so a row cannot read 'Excel, March' and this screen does not
// pretend otherwise." Every clause of that was true when it was written and none
// of it survived api/migrations/108, which put `name`, `venue`, `time_zone` and a
// run of `opens_on`/`closes_on` on the table and gave xp_reader a policy-scoped
// select on it. operator/src/activations.ts has carried all of them on every row
// since. The `<h2>` went on drawing `client.label` under a comment saying in
// capitals that the field did not exist, and that is why the bug reached staging
// and survived being read: the file answered the question before anybody asked
// it. Which is the argument for writing the premise down rather than only the
// rule — a premise can be checked against the schema, and a rule cannot.
//
// THE HEADING IS THE ACTIVATION'S OWN NAME, and the argument is not that the
// newest field wins. Ask what tells one row from another, for each of the two
// readers. For a client viewer every row's client is their own client, so a
// column of client names is one word repeated down the page and separates
// nothing. For a nova_admin the client does separate rows — until one client has
// two shows, which is the ordinary state of a repeat client and is exactly where
// this screen was worst: three activations under one heading, three times, with
// nothing telling them apart but a uuid at the foot of each. The activation's
// name separates them for both readers, and it usually carries the client's name
// inside it, because an event is generally called a client and a month.
//
// WHOSE IT IS IS STILL ON THE ROW rather than displaced by it. They are two
// questions — "which show is this" and "whose is it" — and only one of them fits
// in a heading, so the other is drawn quietly under the server's sentence, in
// client.ts's five states, unflattened. The rule about those states is at the
// line that draws them.
//
// A ROW WITH NO NAME SAYS SO IN WORDS AND BORROWS NOTHING. `name` is nullable,
// nothing on staging has one today, and 108 refuses a blank one precisely so
// that "nobody has said" cannot be confused with "somebody said nothing". A
// heading that fell back to the client label would be the bug above with an extra
// step in it. A heading that fell back to the identifier would be the bug above
// exactly. A blank heading is the third mistake and the worst of them, because it
// reads as a broken page rather than as an unfilled field. So the row says nobody
// has named it, in the class this console renders every other null in, and the
// identifier stays where it already was: at the foot of the row, which is what
// opens it and what a person quotes when asking somebody to fill the name in.
//
// THE VENUE AND THE RUN ARE DRAWN AND THE CLOCK IS NOT. `venue`, `opens_on` and
// `closes_on` are what a person scans for after the name, and the two dates are
// civil dates in the venue's own calendar — the one shape on this row that needs
// no conversion by anybody, which is most of the answer to the section below.
// `time_zone` is deliberately not drawn: activations.ts calls it "the one field
// here a machine reasons with rather than displays", nothing on this screen is
// rendered in it, and printing "Europe/London" beside two civil dates would
// invite a reader to do in their head the arithmetic the section below refuses to
// do in code. The kiosk screen draws it, because that is the screen where a
// judgement is made against it.
//
// THE DATES ARE NOT REFORMATTED HERE and that is the one presentational rule on
// this screen. `first_event_at` and `last_event_at` are instants, and rendering
// an instant in the reader's own zone can move a show a day — a stand that ran
// on the 4th in Dubai reads as the 3rd to somebody in London, on a list whose
// entire job is recognising which show is which. The server already turns the
// two instants into a sentence carrying a date, so that sentence is what a row
// shows.
//
// THE QUESTION WAS PUT AGAIN WHEN 108 LANDED, because the rule above was written
// when the venue's zone was unknowable and the zone is now on the row. So this
// file could render those two instants in the venue's own time and the Dubai
// objection would go away. It is refused, and any one of these is enough on its
// own.
//
//   THE ZONE IS NULLABLE, and null is the only value staging holds. A console
//   that converted where it could and left the instant alone where it could not
//   would draw two kinds of date down one list with nothing saying which is
//   which — the same failure as the one being fixed rather than a smaller one.
//
//   THE DATES THAT MATTER MOST NEED NO CONVERSION AT ALL. `opens_on` and
//   `closes_on` are already civil dates in the venue's calendar, and they answer
//   "when was this" better than the first and last capture do: they are when the
//   show ran, rather than when a tablet happened to fire. They are drawn
//   verbatim, so the recognition this rule exists to protect is done by values no
//   zone applies to.
//
//   THE SERVER HAS ALREADY DECIDED, AND A SECOND DECISION IS A DISAGREEMENT.
//   `says` arrives in the payload whatever this file does with the instants, so a
//   converted date beside it is two statements about the same two instants that
//   can differ by a day. KioskCoverage.tsx's header is the general form of this:
//   the picture and the verdict cannot say different things.
//
//   AND A CONVERTED DATE IS A STRING THE SERVER NEVER SENT. kiosk/ground.ts holds
//   that rule for this same activation — "every `HH:MM` and every `YYYY-MM-DD`
//   that leaves here came out of the server's answer verbatim" — and check.mjs
//   proves it by finding each one in the answer. Converting here would make this
//   the one place in the package where a date on the screen cannot be found in
//   the response that produced it, and Intl's answer for a zone this browser's
//   ICU data does not carry is a silent fallback rather than a refusal.
//
// WHETHER THE SHOW HAS BEEN AND GONE IS IN THAT SAME SENTENCE, and this file
// does not form a second opinion about it. `closed_at` and `sealed_at` travel
// on the row; `says` is already the server's reading of them — not recorded as
// closed, closed, or sealed — and drawing a badge here from the timestamps
// would be two judgements that can disagree. A null is not "live". The event
// dates still must not be used as a close: a stand with no captures for a day
// looks identical to a closed one, which is why the server refused that
// inference and why this screen must not invent it.
//
// AND THE DAY-BOUNDARY HAZARD THE RULE NAMES IS NOT ACTUALLY GONE, which is
// worth writing down here rather than fixing here. `describeLine` builds the
// event half of its sentence by slicing ten characters off an instant that
// db.ts rendered `at time zone 'utc'`, so the date in it is the UTC date and
// not the venue's: a Chicago stand whose last capture was at 18:00 local is
// reported as closing the following day. Deferring to the server does not
// remove that problem, it moves it to a fixed zone — which is still better
// than the reader's, because it is the same for everybody looking at the row,
// and a list where two people see different dates is worse than one where
// everybody sees the same slightly wrong one. The repair is `describeLine`
// reading the `time_zone` that `listActivations` already holds in the same
// function, and it belongs in xp/operator so that one sentence stays the only
// answer. It is not done in a browser.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { PageHead } from "@nova/desk-ui/parts";
import { Empty, SecLabel, Seg } from "@nova/desk-ui/kit";
import { DeskPage, SortHead, useListSort, type ListSort } from "@nova/desk-ui/page";
import { ListState, Loading } from "@nova/desk-ui/states";
import type { ActivationDirectory, ActivationLine, ActivationSortKey } from "../../../operator/src/activations.js";
import { readActivations, type ApiOptions, type Failure } from "../api/client.js";
import { activationName } from "./activationName.js";
import { lifecycle, stateParam, STATE_TAG, type ShowState } from "./lifecycle.js";
import { AS_SENT, asked, type AsSent } from "./listOrder.js";
import { OpenShow } from "./OpenShow.js";
import { ReadFailed } from "./ReadFailed.js";
import { opens } from "./roleAccess.js";
import type { SessionToken } from "../session/store.js";

export type ChooseProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly onOpen: (activationId: string) => void;
  /**
   * Called with the sentence to show on the login screen when the session died.
   *
   * Same contract as every other read surface: one place owns the session and it
   * is App. A 401 here means the same as a 401 on a report, and this screen is
   * the first read a person makes after signing in, so it is the one most likely
   * to meet a session that expired while the tab was open.
   */
  readonly onSessionEnded: (detail: string) => void;
  /** The session's role, used to decide whether a 403 is offered enrolment — and for nothing else. */
  readonly role: string;
  /** The enrolment screen. Offered from a refusal — Settings is where it lives. */
  readonly onEnrol: () => void;
};

type State =
  | { readonly phase: "reading" }
  | { readonly phase: "read"; readonly directory: ActivationDirectory }
  | { readonly phase: "failed"; readonly failure: Failure };

export function Choose({ api, token, onOpen, onSessionEnded, role, onEnrol }: ChooseProps): ReactNode {
  const [state, setState] = useState<State>({ phase: "reading" });
  // Bumped after a show is opened so this screen re-reads the list. The new row
  // is the server's. A local prepend would be a second directory this file
  // invented, and navigating into the show would prefetch a dashboard.
  const [generation, setGeneration] = useState(0);
  // THE ORDER IS THE SERVER'S, ASKED FOR AND NOT APPLIED HERE (12 Oct
  // hand-off: every column sorts, on the server). At rest the directory is in
  // its own order, by last event, which is by no column, so no head claims it.
  const order = useListSort<ActivationSortKey | AsSent>(DIRECTORY_REST);
  const chosen = asked<ActivationSortKey>(order);
  const chosenKey = chosen?.key ?? null;
  const chosenDir = chosen?.dir ?? null;
  // THE STATE FILTER IS THE SERVER'S TOO (151): the list is asked for one state
  // and the counts beside it come back with it, over every state.
  const [stateFilter, setStateFilter] = useState<StateFilter>("All");

  useEffect(() => {
    // `cancelled` rather than an AbortController, for the reason Operator.tsx
    // gives: this read is recorded in report_access whether or not the component
    // is still mounted, and aborting it would leave an audit row marked failed
    // for a disclosure that happened.
    let cancelled = false;

    // A refresh after opening a show, or for a new order, keeps the list on
    // screen. Flipping back to "Finding your activations…" would unmount the
    // form that just succeeded and look like the create had been lost.
    setState((now) => (now.phase === "read" ? now : { phase: "reading" }));

    void (async () => {
      const result = await readActivations<ActivationDirectory>(
        api,
        token,
        chosenKey === null || chosenDir === null ? null : { key: chosenKey, dir: chosenDir },
        stateFilter === "All" ? null : stateParam(stateFilter),
      );
      if (cancelled) return;

      if (result.ok) {
        setState({ phase: "read", directory: result.value });
        return;
      }

      setState({ phase: "failed", failure: result });
      if (result.kind === "session_ended") onSessionEnded(result.detail);
    })();

    return () => {
      cancelled = true;
    };
  }, [api, token, onSessionEnded, generation, chosenKey, chosenDir, stateFilter]);

  // THE DESIGN'S LIST STATES (console-screens.jsx, ListState): the head, Add
  // activation and Find an activation are drawn while the list is read and
  // when it fails, and only the answer under "In the server's order" changes.
  // A failed read used to replace the whole page with a refusal of its own.
  const answer =
    state.phase === "reading" ? (
      <Loading rows={6} cols={4} label="Finding your activations…" />
    ) : state.phase === "failed" ? (
      <ReadFailed
        onRetry={() => {
          setState({ phase: "reading" });
          setGeneration((n) => n + 1);
        }}
        what="Could not load activations"
        failure={state.failure}
        role={role}
        onEnrol={onEnrol}
        // The same rule as a refused report, phrased for the one screen where
        // the empty version of it is a real answer. A viewer with no grants gets
        // a 200 and an empty list on purpose; this is the other case, and
        // reading it as "you have no activations" would tell somebody who has
        // six that they have none.
        notCounted="The list did not load. This does not mean you have no activations."
      />
    ) : null;

  const directory = state.phase === "read" ? state.directory : null;

  return (
    <Directory
      directory={directory}
      answer={answer}
      order={order}
      stateFilter={stateFilter}
      onStateFilter={setStateFilter}
      onOpen={onOpen}
      openShow={
        opens({ role, canExport: null }, "addActivation") ? (
          // Offered here and not on Settings, to whoever roleAccess.ts gives
          // `addActivation`: an Admin, and an Editor for a client they hold,
          // whose picker lists only those (client-choices). "New client" is
          // `addClient`, an Admin's. A grant is a grant to read a show, not to
          // add one. The form fetches nothing until it is opened.
          <OpenShow
            api={api}
            token={token}
            onSessionEnded={onSessionEnded}
            onOpened={() => setGeneration((n) => n + 1)}
            newClient={opens({ role, canExport: null }, "addClient")}
          />
        ) : null
      }
    />
  );
}

/** The directory at rest: the service's own order, which is by no column. */
const DIRECTORY_REST: ListSort<ActivationSortKey | AsSent> = { key: AS_SENT, dir: "asc" };

type DirectoryHeads = {
  readonly sort: ListSort<ActivationSortKey | AsSent>;
  readonly lit: boolean;
  readonly onSort: (key: ActivationSortKey | AsSent) => void;
};

/** Tiles or a table. The same rows either way; only the shape changes. */
type View = "Tiles" | "List";

/** All, or one of the four states the service files a show under. See lifecycle.ts. */
type StateFilter = "All" | ShowState;

const STATE_FILTERS: readonly StateFilter[] = ["All", "Open", "Paused", "Closed", "Sealed"];
const VIEWS: readonly View[] = ["Tiles", "List"];
const SHOWN_STATES: readonly ShowState[] = ["Open", "Paused", "Closed", "Sealed"];

/**
 * The list, its filters and the sentence that says how many there are.
 *
 * DRAWN IN ALL FOUR STATES, AS THE DESIGN'S ListState IS: reading, failed,
 * empty and full. `directory` is null until the list has been read, and
 * `answer` is what stands where the list would be while it is (the skeleton,
 * or the inline Failed with Ask again). Filters stay drawn throughout, so the
 * page does not move under the reader when the rows land.
 */
function Directory({
  directory,
  answer,
  order,
  stateFilter,
  onStateFilter,
  onOpen,
  openShow,
}: {
  readonly directory: ActivationDirectory | null;
  readonly answer: ReactNode;
  readonly order: DirectoryHeads;
  readonly stateFilter: StateFilter;
  readonly onStateFilter: (state: StateFilter) => void;
  readonly onOpen: (activationId: string) => void;
  readonly openShow: ReactNode;
}): ReactNode {
  const [query, setQuery] = useState("");
  const [view, setView] = useState<View>("Tiles");
  const activations = useMemo(() => directory?.activations ?? [], [directory]);

  const term = query.trim().toLowerCase();
  const shown = useMemo(
    () =>
      // The state was narrowed by the server; the search narrows what came back.
      activations.filter((activation) => term === "" || haystack(activation).includes(term)),
    [activations, term],
  );

  // Over every state, from the server, so a filtered list still says what the
  // others hold. A service that predates the counts is counted from the rows.
  const counts = directory?.state_counts;
  const count = (state: ShowState): number =>
    counts !== undefined
      ? (counts[stateParam(state) as keyof typeof counts] ?? 0)
      : activations.filter((activation) => lifecycle(activation) === state).length;
  const total = SHOWN_STATES.reduce((sum, state) => sum + count(state), 0);

  // The server's own sentence about what this list is, including when it is
  // empty. It distinguishes "every activation in this database" from "the ones
  // this credential may read", which is a distinction the console must not
  // restate in words of its own — a nova_admin and a client viewer are looking
  // at very different pages. The lede stays on the page, as the October
  // hand-off keeps it here; the access note under it went with the hand-off,
  // which draws this head with no note. Until the list is read there is no
  // sentence, and none is invented in its place.
  const head = (
    <>
      <PageHead
        title="Activations"
        {...(directory === null ? {} : { lede: directory.says ?? DIRECTORY_UNSAID })}
      />
      {openShow}
    </>
  );

  // THE 12 OCTOBER PAGE SHAPE. Find an activation is the right-hand column,
  // with the count; the list beside it; the title and Open a show across the
  // top. The filters narrow what is drawn; the order is the server's, and a
  // head in the List view asks it for another.
  return (
    <DeskPage
      head={head}
      side={
        <section>
          <SecLabel>Find an activation</SecLabel>
          <div className="panel panel-stack">
            <div className="filters">
              <label className="field">
                <span className="label">Search</span>
                <input
                  className="input"
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Name, client, venue or UUID"
                />
              </label>
              <div className="field">
                <span className="label">State</span>
                <Seg label="State" value={stateFilter} options={STATE_FILTERS} onPick={onStateFilter} />
              </div>
              <div className="field">
                <span className="label">View</span>
                <Seg label="View" value={view} options={VIEWS} onPick={setView} />
              </div>
            </div>
            {/* A count of rows in hand, not a figure about any show, so it is
                drawn only once there are rows, as the design draws it. The
                header says why that is a different thing, and why this line
                is what makes an empty filter result readable. The count is
                its own live region, so a reader filtering by keyboard hears
                the new figure; the sentence after it is the design's, and
                says what a filter does not do. */}
            {total === 0 ? null : (
              <p className="help help-tight">
                <span aria-live="polite">
                  {total} {total === 1 ? "activation" : "activations"} · {count("Open")} open · {count("Paused")} paused ·{" "}
                  {count("Closed")} closed · {count("Sealed")} sealed
                </span>
                . A filter changes what is shown, not the order, and the order is the server&apos;s.
              </p>
            )}
          </div>
        </section>
      }
    >
      <section>
        <SecLabel>In the server&apos;s order</SecLabel>
        {directory === null ? (
          answer
        ) : (
          <ListState
            state="ready"
            rows={shown}
            cols={6}
            unit="activations"
            empty={
              total === 0 ? (
                <Nothing directory={directory} />
              ) : (
                <Empty title="No activations match">
                  <p>Try another name, client, venue or ID.</p>
                </Empty>
              )
            }
          >
            {/* Drawn in the order the server sorted them. See the header. */}
            {view === "List" ? (
              <Table rows={shown} order={order} onOpen={onOpen} />
            ) : (
              <ul className="dir">
                {shown.map((activation) => (
                  <Row key={activation.activation_id} activation={activation} onOpen={onOpen} />
                ))}
              </ul>
            )}
          </ListState>
        )}
      </section>
    </DeskPage>
  );
}

/**
 * Where an activation is in its life, from the two timestamps that decide it.
 *
 * A SEAL OUTRANKS A CLOSE, which is activations.ts's own rule — "a seal without
 * a close is still a seal" — so it is tested first. The three answers are the
 * filter's labels and nothing else: the sentence on the row stays the server's.
 */
// lifecycle and STATE_TAG moved to ./lifecycle.ts, which is where the ruling on
// the design's active/paused/archived is now written down. Show details states
// the same fact about the same row, and two pages carrying their own copies of
// three tests is how one of them quietly gains a case the other has not.

/**
 * What the search box reads.
 *
 * The four things a person would type to find a show they know exists: what it
 * is called, whose it is, where it is, and the identifier somebody pasted them.
 * Not the server's sentences — a search that matched "closed" inside `says`
 * would quietly duplicate the state filter and disagree with it.
 */
function haystack(activation: ActivationLine): string {
  return [
    said(activation.name),
    said(activation.client?.label),
    said(activation.venue),
    activation.activation_id,
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * One activation: what it is, where and when it runs, whose it is, and its
 * identifier — in that order, and every line of it a string the server sent.
 *
 * A LIST ITEM WITH A BUTTON RATHER THAN A LINK, which is a real trade rather
 * than a shrug. A link would be right-clickable and openable in a new tab, and
 * that is worth something on a console people bookmark; it would also mean a
 * middle-click on a list of six activations filing six `report_access` rows for
 * reports nobody read. The address is bookmarkable once open — the operator
 * screen says so — so what is lost is the shortcut and not the property.
 */
function Row({
  activation,
  onOpen,
}: {
  activation: ActivationLine;
  onOpen: (activationId: string) => void;
}): ReactNode {
  const heading = activationName(activation.name);
  const whereAndWhen = [said(activation.venue), runOf(activation)].filter((part) => part !== "");
  const state = lifecycle(activation);

  return (
    <li>
      <div>
        {/* WHAT THE EVENT IS, OR THAT NOBODY HAS SAID. Both are statements about
            the activation; neither is the client's name and neither is a uuid.
            See the header for why this is the heading rather than the client, and
            for why a blank, a borrowed name and an identifier are all refused.

            An absent field and a null are drawn the same, deliberately. They
            differ only against a service older than 108, which no deployment is
            and which the type this component is compiled against does not
            describe — and "nobody has named this" is what a reader needs to know
            in both cases. */}
        {/* The tag beside it is the filter's own label, from the same two
            timestamps the sentence below was drawn from. See lifecycle(). */}
        <div className="dir-top">
          <h2 className={heading.unnamed ? "unnamed" : undefined}>{heading.text}</h2>
          <span className={`tag ${STATE_TAG[state]}`}>{state}</span>
        </div>

        <Whose client={activation.client} />

        {/* WHERE IT IS AND WHEN IT RUNS, in the server's own characters. The run
            is two civil dates in the venue's calendar joined by a word; nothing
            is parsed, nothing is localised and no month is spelled out, because
            the moment a date on this row is rendered rather than echoed it stops
            being findable in the answer that produced it. Absent entirely rather
            than drawn as dashes where the activation says neither, on the same
            reasoning as the heading: an em dash in a gap is where "unstated" and
            "stated as nothing" get thrown away at the last step. */}
        {whereAndWhen.length === 0 ? null : (
          <p className="dir-when">{whereAndWhen.join(" · ")}</p>
        )}

        {/* The server's sentence, including whether the show is not recorded as
            closed, closed, or sealed. Not rewritten here: see the header. */}
        <p className="dir-state">{activation.says ?? ""}</p>
        <p className="id">{activation.activation_id}</p>
      </div>
      <div>
        <button type="button" className="btn" onClick={() => onOpen(activation.activation_id)}>
          Open
        </button>
      </div>
    </li>
  );
}

/**
 * The same rows as a table, for somebody comparing across shows rather than
 * looking for one.
 *
 * THE COLUMNS ARE THE TILE'S OWN FACTS AND NOT MORE OF THEM. A table invites a
 * column per field, and the fields not here are the ones the header refuses:
 * every count belongs to a report that records its own read. What a wider view
 * buys is alignment, not disclosure.
 *
 * The server's sentence does not fit a cell and is not truncated into one —
 * a clipped "closed and has not been sealed" reads as "closed", which is the one
 * distinction that sentence exists to make. It stays on the tile, and the state
 * column carries the same fact in a word.
 */
function Table({
  rows,
  order,
  onOpen,
}: {
  readonly rows: readonly ActivationLine[];
  readonly order: DirectoryHeads;
  readonly onOpen: (activationId: string) => void;
}): ReactNode {
  return (
    <div className="tablewrap">
      <div className="tablescroll">
        <table aria-label="Activations">
          <thead>
            {/* Every column sorts, and the server sorts: the keys are
                ACTIVATION_SORT_KEYS. Open is a control. */}
            <tr>
              <SortHead of="activation" {...order}>
                Activation
              </SortHead>
              <SortHead of="client" {...order}>
                Client
              </SortHead>
              <SortHead of="venue" {...order}>
                Venue
              </SortHead>
              <SortHead of="when" {...order}>
                When
              </SortHead>
              <SortHead of="state" {...order}>
                State
              </SortHead>
              <th scope="col">
                <span className="vh">Open</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((activation) => {
              const heading = activationName(activation.name);
              const run = runOf(activation);
              const venue = said(activation.venue);
              const client = said(activation.client?.label);
              return (
                <tr key={activation.activation_id}>
                  <td data-label="Activation">
                    <span
                      className={heading.unnamed ? "t-id quiet" : "t-id"}
                      style={{ fontFamily: "var(--d-sans)", fontSize: "14.5px" }}
                    >
                      {heading.text}
                    </span>
                    <p className="id" style={{ marginTop: 6 }}>
                      {activation.activation_id}
                    </p>
                  </td>
                  <td data-label="Client">{client === "" ? <Unrecorded /> : client}</td>
                  <td data-label="Venue">{venue === "" ? <Unrecorded /> : venue}</td>
                  <td data-label="When" className="num">
                    {run === "" ? <Unrecorded /> : run}
                  </td>
                  <td data-label="State">
                    <span className={`tag ${STATE_TAG[lifecycle(activation)]}`}>{lifecycle(activation)}</span>
                  </td>
                  <td data-label="">
                    <button
                      type="button"
                      className="btn btn-quiet btn-sm"
                      onClick={() => onOpen(activation.activation_id)}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * A cell the activation says nothing in.
 *
 * The same distinction the tile makes by omitting the line: a table cannot omit
 * a cell, so it says which kind of nothing this is instead of drawing a dash.
 */
function Unrecorded(): ReactNode {
  return <span className="unk">not recorded</span>;
}

/**
 * Whose activation this is, in the five states client.ts decides and no others.
 *
 * `label` is the client's name where one has been recorded and an identifier
 * where it has not — `clientLineFrom`'s own five states, resolved by the server
 * so that this screen holds no second opinion about whose an activation is.
 * `says` beside it is the difference between "no name recorded yet" and "not
 * yours to know", which is a distinction a null would lose and which a client
 * viewer must not be able to draw from what is on the screen: `unnamed` and
 * `unrecorded` both put the client's identifier in `label`, and the second of
 * them is 010's `client_own` policy declining to hand over a row. Nothing here
 * may render those two differently, so nothing here reads `status` at all.
 *
 * THE LABEL IS DRAWN ONLY WHERE THE SERVER GAVE A CLIENT ID, which is new and is
 * a consequence of the heading moving. In `unknown` and `disputed` there is no
 * client to name and `label` is the *activation's* id — clientLineFrom's
 * deliberate failure direction, chosen over inventing a name. That was harmless
 * while the label was the heading and the identifier at the foot of the row was
 * the same string. It is not harmless as a line captioned "whose is this": it
 * would print the activation's own identifier as the answer to a question about a
 * client, twice on one row, and read as a fault. Both of those states carry a
 * `says` that stands alone — "nothing here says which client it is for", "events
 * from 2 different clients are filed under this one activation: …" — whereas
 * `unnamed` and `unrecorded` say "this is the identifier" and need it beside
 * them. So the test is `client_id`, which is the server's own statement that
 * there is a client here, and not `status`, which is the field the two withheld
 * states differ in.
 *
 * AND AN IDENTIFIER IS DRAWN AS AN IDENTIFIER. `name` is null unless a real name
 * was recorded — client.ts put it there so a caller could tell the two apart
 * without reading `status` — so a label that is not a name gets the class every
 * other identifier on this console gets. 010's argument, applied to type rather
 * than to wording: a placeholder that looks like a company name puts words Nova
 * invented in front of the client they describe. It is the same for `unnamed` and
 * `unrecorded`, so it draws no distinction the paragraph above forbids.
 */
function Whose({ client }: { client: ActivationLine["client"] | undefined }): ReactNode {
  const says = said(client?.says);
  const label = said(client?.client_id) === "" ? "" : said(client?.label);
  const isName = said(client?.name) !== "";

  if (label === "" && says === "") {
    // Named rather than left as an empty paragraph, for DIRECTORY_UNSAID's
    // reason. A row that says nothing at all about whose it is, on the screen
    // that answers "what may I read", is the reader's cue to guess.
    return <p className="dir-client">{CLIENT_UNSAID}</p>;
  }

  return (
    <p className="dir-client">
      {label === "" ? null : (
        <>
          <span className={isName ? undefined : "mono"}>{label}</span>
          {says === "" ? null : " · "}
        </>
      )}
      {says}
    </p>
  );
}

/**
 * The run of the show, as the two civil dates the activation states.
 *
 * Both or neither: a run with one end is not a run, and drawing "from 2026-03-04"
 * with nothing after it invites the reader to supply the other end. One day where
 * the two are equal, because "2026-03-04 to 2026-03-04" reads as a formatting
 * fault rather than as a one-day stand.
 *
 * No arithmetic and no comparison beyond equality. The number of days is a count
 * this file worked out, which is the one thing a row may not carry — see the
 * no-counts rule in the header, and the check that reads every run of digits on
 * the page back against the server's own answer.
 */
function runOf(activation: ActivationLine): string {
  const opensOn = said(activation.opens_on);
  const closesOn = said(activation.closes_on);

  if (opensOn === "" || closesOn === "") return "";
  return opensOn === closesOn ? opensOn : `${opensOn} to ${closesOn}`;
}

/**
 * What the server actually said, or the empty string.
 *
 * Trimmed, and that is not tidiness. 108 refuses a `name` that is whitespace on
 * the argument that a blank "renders as absent on every surface, compares as
 * present in every `is not null`, and is the shape a form with a required field
 * and a trimming bug produces every time". A console that trusted the constraint
 * would draw a heading of three spaces the day something writes around it, which
 * is the blank heading this file refuses two paragraphs above. kiosk/ground.ts
 * takes the same reading of the same fields, so an activation this screen calls
 * nameless is one that screen calls nameless too.
 */
const said = (value: string | null | undefined): string => (typeof value === "string" ? value.trim() : "");

/**
 * An empty list, which is an answer rather than a failure.
 *
 * THE SENTENCE IS THE SERVER'S AND THE HEADING DOES NOT CONTRADICT IT. A viewer
 * who is properly scoped and holds no grants gets a 200 and an empty list, on
 * activations.ts's argument that the true answer to "which activations may I
 * read" is "none" and that a 403 would say "you may not ask", which is false and
 * leaves them nowhere. So this must not read as a refusal, must not read as an
 * error, and must not read as an empty screen either — it says what is true
 * about their grants and what would change it, which is somebody issuing one.
 *
 * `says` covers both the scoped case and the service one, where an empty list
 * means nothing has been opened in this database at all. Two headings for two
 * pages that mean different things, one sentence underneath from whoever knows
 * which of them this is.
 */
function Nothing({ directory }: { directory: ActivationDirectory }): ReactNode {
  const kind = directory.access?.kind;

  // A HEADING AND NOTHING ELSE, WHICH IS SMALLER THAN IT WAS AND SAYS MORE.
  //
  // This drew two further things and has stopped drawing both, for two unrelated
  // reasons that happened to land together.
  //
  // The reassurance — "nothing has been refused. This is the list itself, and it
  // is empty" — was written here behind `kind === "denied" ? null :`, because
  // describeList sent one sentence for two opposite situations and this was the
  // only place that could tell them apart. It was the one line on this screen
  // where the console contradicted the server rather than drawing it. The server
  // now sends three distinct sentences and carries the reassurance itself, so the
  // right reader gets it from the position of their row in the data rather than
  // from a browser reading `access.kind` back out of a response and deciding.
  //
  // `says` went for a duller reason: Choose already draws it as the lede above,
  // on every render, so an empty list printed it twice. That predates all of this
  // and was invisible only because the sentence used to be short.
  //
  // What is left is the one thing here that is genuinely the console's own: which
  // of three headings an empty page gets. A Nova admin looking at an empty estate,
  // a viewer waiting for a grant and a credential matching no policy are three
  // different pages, and none of the three headings contradicts the server.
  //
  // DRAWN AS THE KIT'S Empty, UNDER THE FILTERS, as the design draws an empty
  // directory (console-screens.jsx). It used to be a quiet notice on a page
  // with no right-hand column, which the design does not have.
  return (
    <Empty title={EMPTY_HEADING[kind ?? ""] ?? EMPTY_HEADING["scoped"] ?? ""}>
      {kind === "service" ? (
        <p>An activation is created by Nova against a client record. Nothing has been created yet.</p>
      ) : null}
    </Empty>
  );
}

/**
 * The heading over an empty list, per kind of reader.
 *
 * THREE HEADINGS BECAUSE THERE ARE THREE PAGES THAT MEAN DIFFERENT THINGS, and
 * the sentence underneath cannot carry the difference on its own: a Nova admin
 * looking at an empty estate, a client viewer waiting for a grant, and a
 * credential that matches no policy are all told "the list is empty" by the same
 * JSON. Keyed loosely so that a fourth kind added to DirectoryAccess falls
 * through to the scoped wording rather than to a blank heading — the honest
 * default of the three, since it claims the least.
 */
const EMPTY_HEADING: Record<string, string> = {
  // The design's title for the estate with nothing on it (desks.mdc: the
  // prototype's empty-state titles are final).
  service: "No activation is on this service",
  scoped: "No activations assigned",
  denied: "You cannot view activations",
};

/**
 * What is drawn where the server sent no sentence.
 *
 * A build of the service older than the field, which is the one case that
 * produces it. Named rather than left as an empty paragraph, because an empty
 * paragraph on the screen that answers "what may I read" is the reader's cue to
 * guess.
 */
const DIRECTORY_UNSAID =
  "This list is unavailable because Console and the service are on different versions.";

/**
 * The heading over an activation nobody has named.
 *
 * A SENTENCE RATHER THAN A DASH, AND ABOUT THE ACTIVATION RATHER THAN ABOUT THE
 * SCREEN. "Untitled" is a word about a document; "—" is a gap somebody fills in
 * with a guess. What is true is that nobody has said yet, which is an ordinary
 * state before a stand opens and stays ordinary for a while afterwards — every
 * activation in this database is in it today, because 108 added the column with
 * no backfill and nothing yet writes it.
 *
 * It is identical on every nameless row, which is the honest cost of it: a
 * nova_admin looking at an estate nobody has named reads the same heading down
 * the page. That is what the data says. The row's other facts — the venue, the
 * run, whose it is, and the identifier at the foot — are what tells those rows
 * apart until somebody names them, which is the only thing that actually fixes
 * it and is a write this console does not yet have. The sentence itself is
 * activationName.ts, so the rail and the page top draw the same words.
 */

/**
 * What is drawn where the server sent no client at all.
 *
 * Not one of client.ts's five states — every one of those arrives with a `says` —
 * so this is the same case as DIRECTORY_UNSAID and gets the same kind of
 * sentence: a build of the service older than the field, said in words rather
 * than left as an empty line under a heading.
 */
const CLIENT_UNSAID =
  "Client details are unavailable because Console and the service are on different versions.";
